import { ThemeConfig } from './theme';
import { applyTransformOnKeys } from '../utils/transform';
import { hex2rgba } from '../utils';
import { ComponentValueColor } from './components';

export type HEX = `#${string}`;

export type ColorsOldConfig = {
  'Primary/10': HEX;
  'Primary/20': HEX;
  'Primary/30': HEX;
  'Primary/50': HEX;
  'Primary/70': HEX;
  'Primary/80': HEX;
  'Primary/90': HEX;
  'Secondary/10': HEX;
  'Secondary/20': HEX;
  'Secondary/30': HEX;
  'Secondary/50': HEX;
  'Secondary/70': HEX;
  'Secondary/80': HEX;
  'Secondary/90': HEX;
  'Neutral/0': HEX;
  'Neutral/10': HEX;
  'Neutral/20': HEX;
  'Neutral/30': HEX;
  'Neutral/50': HEX;
  'Neutral/70': HEX;
  'Neutral/80': HEX;
  'Neutral/90': HEX;
  'Neutral/100': HEX;
  // @deprecated use Primary/10 instead
  'Blue/10': HEX;
  // @deprecated use Primary/20 instead
  'Blue/20': HEX;
  // @deprecated use Primary/30 instead
  'Blue/30': HEX;
  // @deprecated use Primary/50 instead
  'Blue/50': HEX;
  // @deprecated use Primary/70 instead
  'Blue/70': HEX;
  // @deprecated use Primary/80 instead
  'Blue/80': HEX;
  // @deprecated use Primary/90 instead
  'Blue/90': HEX;
  'Purple/10': HEX;
  'Purple/20': HEX;
  'Purple/30': HEX;
  'Purple/50': HEX;
  'Purple/70': HEX;
  'Purple/80': HEX;
  'Purple/90': HEX;
  'Teal/10': HEX;
  'Teal/20': HEX;
  'Teal/30': HEX;
  'Teal/50': HEX;
  'Teal/70': HEX;
  'Teal/80': HEX;
  'Teal/90': HEX;
  'Green/10': HEX;
  'Green/20': HEX;
  'Green/30': HEX;
  'Green/50': HEX;
  'Green/70': HEX;
  'Green/80': HEX;
  'Green/90': HEX;
  'Yellow/10': HEX;
  'Yellow/20': HEX;
  'Yellow/30': HEX;
  'Yellow/50': HEX;
  'Yellow/70': HEX;
  'Yellow/80': HEX;
  'Yellow/90': HEX;
  'Red/10': HEX;
  'Red/20': HEX;
  'Red/30': HEX;
  'Red/50': HEX;
  'Red/70': HEX;
  'Red/80': HEX;
  'Red/90': HEX;
};

export type PredefinedColor = keyof ColorsOldConfig;
export type Color = PredefinedColor | HEX;

export type ColorPair = {
  light: HEX;
  dark: HEX;
  opacity?: number;
};

export enum PredefinedColorNext {
  'brand/default' = 'brand/default',
  'brand/surface' = 'brand/surface',
  'brand/accent' = 'brand/accent',
  'brand/tint' = 'brand/tint',
  'neutral/default' = 'neutral/default',
  'neutral/alt' = 'neutral/alt',
  'background/default' = 'background/default',
  'background/alt' = 'background/alt',
  'background/shadow' = 'background/shadow',
  'success/default' = 'success/default',
  'error/default' = 'error/default',
}

export type ColorsNextConfig = Record<
  PredefinedColorNext,
  Omit<ColorPair, 'opacity'>
>;

export type ColorNext = keyof typeof PredefinedColorNext | ColorPair;

export type ColorsConfig = {
  legacy: ColorsOldConfig;
  next: ColorsNextConfig;
  pinwheelBrandColor: {
    legacy: HEX;
    next: Omit<ColorPair, 'opacity'>;
  };
};

/**
 * @deprecated Use the `opacityNext` property for component color definitions instead.
 */
export const getColorNextWithOpacity = (
  colorsConfig: ColorsNextConfig,
  color: ColorNext,
  opacity: number
): ColorPair => {
  if (typeof color === 'string') {
    return {
      ...colorsConfig[color as PredefinedColorNext],
      opacity,
    };
  }
  return { ...color, opacity };
};

export const colorProperties = [
  'backgroundColor',
  'color',
  'borderColor',
] as const;
export type ColorProperty = (typeof colorProperties)[number];

export type ColorResolveOptions =
  | {
      type: 'raw';
      colorPair: ColorPair;
    }
  | {
      type: 'theme';
      colorLegacy: Color;
      opacityLegacy?: number;
      colorNext: ColorNext;
      opacityNext?: number;
    }
  | {
      type: 'component';
      value: ComponentValueColor;
    }
  | {
      type: 'pinwheel';
    };

const getModeColor = (color: ColorPair, isDarkMode: boolean) =>
  isDarkMode ? color.dark : color.light;

export const resolveColor =
  (theme: ThemeConfig, useNext: boolean, isDarkMode: boolean) =>
  (colorOptions: ColorResolveOptions): string => {
    if (colorOptions.type === 'raw') {
      const outColor = getModeColor(
        colorOptions.colorPair,
        isDarkMode && useNext
      );
      return colorOptions.colorPair.opacity !== undefined
        ? hex2rgba(outColor, colorOptions.colorPair.opacity)
        : outColor;
    }

    if (colorOptions.type === 'pinwheel') {
      if (useNext) {
        return getModeColor(theme.colors.pinwheelBrandColor.next, isDarkMode);
      }
      return theme.colors.pinwheelBrandColor.legacy;
    }

    let colorLegacy: Color | string;
    let colorNext: ColorNext;
    let opacityLegacy: number | undefined;
    let opacityNext: number | undefined;
    if (colorOptions.type === 'theme') {
      colorLegacy = colorOptions.colorLegacy;
      colorNext = colorOptions.colorNext;
      opacityLegacy = colorOptions.opacityLegacy;
      opacityNext = colorOptions.opacityNext;
    } else {
      colorLegacy = colorOptions.value.legacy;
      colorNext = colorOptions.value.next;
      opacityLegacy = colorOptions.value.opacityLegacy;
      opacityNext = colorOptions.value.opacityNext;
    }
    if (useNext) {
      if (!colorNext) {
        return '';
      }
      if (typeof colorNext === 'string') {
        if (colorNext in theme.colors.next) {
          const outColor = theme.colors.next[colorNext as PredefinedColorNext];
          const out = getModeColor(outColor, isDarkMode);
          return opacityNext !== undefined ? hex2rgba(out, opacityNext) : out;
        }
        return ''; // Should never happen with properly formed data
      }
      const outColor = getModeColor(colorNext, isDarkMode);
      const outOpacity =
        opacityNext !== undefined
          ? opacityNext
          : colorNext.opacity !== undefined
          ? colorNext.opacity
          : undefined;
      return outOpacity !== undefined
        ? hex2rgba(outColor, outOpacity)
        : outColor;
    }

    // useNext === false
    if (colorLegacy in theme.colors.legacy) {
      const colorKey = colorLegacy as PredefinedColor;
      return opacityLegacy !== undefined
        ? hex2rgba(theme.colors.legacy[colorKey], opacityLegacy)
        : theme.colors.legacy[colorKey];
    }
    return colorLegacy;
  };

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const resolveColorProperties = (
  theme: ThemeConfig,
  useNext: boolean,
  isDarkMode: boolean
) =>
  applyTransformOnKeys<Record<ColorProperty, ColorResolveOptions>>(
    colorProperties,
    (colorValue) => {
      const rs = resolveColor(theme, useNext, isDarkMode);

      return rs(colorValue);
    }
  );
